// Cliente de Cognito para el frontend (SRP, sin secreto de cliente).
//
// Estos IDs NO son secretos: son los mismos identificadores públicos que usa el
// backend (backend/lambda/api/config.ts) y que viajan en cualquier bundle. Lo que
// protege el sistema es la firma del JWT, que solo Cognito produce.
//
// Si alguna vez se recrea el User Pool, actualizar aquí Y en config.ts del backend
// — son el mismo par de valores (salidas de `npx cdk deploy IkidsAuth`).

import { Amplify } from "aws-amplify";
import {
  confirmResetPassword,
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  type SignInOutput,
} from "aws-amplify/auth";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { sessionStorage as almacenPorPestana } from "aws-amplify/utils";

export const COGNITO = {
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID ?? "us-east-1_h4bew6Xhr",
  webClientId: import.meta.env.VITE_COGNITO_WEB_CLIENT_ID ?? "5c026hq901b0hrm984494tji1l",
} as const;

/*
 * El app client solo permite ALLOW_USER_SRP_AUTH + ALLOW_REFRESH_TOKEN_AUTH, que es
 * exactamente lo que hace v6 por defecto: `signIn` negocia SRP y la contraseña nunca
 * sale del navegador. No cambiar el flujo por defecto sin tocar también los
 * ExplicitAuthFlows del pool.
 *
 * La sesión de la aplicación vive en sessionStorage (lib/auth.ts) y la de Amplify
 * —la que sabe renovar— TAMBIÉN, en la misma pestaña. Son dos capas distintas, pero
 * tienen que tener el MISMO alcance.
 *
 * Antes Amplify usaba su valor por defecto, localStorage, que comparten todas las
 * pestañas. Con dos cuentas abiertas en dos pestañas (una bibliotecaria que prueba
 * con una cuenta de participante, por ejemplo), la última en entrar sobrescribía
 * los tokens de la otra, y al renovar a los 60 minutos la primera pestaña recibía
 * el token de OTRA persona: seguía viendo su nombre y actuaba como la otra.
 *
 * De paso, el refresh token (30 días) ya no se queda en el disco de una computadora
 * pública de biblioteca: muere al cerrar la pestaña, como la sesión.
 *
 * `setKeyValueStorage` va DESPUÉS de `configure`: es lo que indica la documentación
 * de v6, y `configure` no lo pisa.
 */
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: COGNITO.userPoolId,
      userPoolClientId: COGNITO.webClientId,
    },
  },
});
cognitoUserPoolsTokenProvider.setKeyValueStorage(almacenPorPestana);

/*
 * Lo que dejaron las versiones anteriores en localStorage: tokens de Cognito —el
 * refresh incluido— de quien entró por última vez en esta máquina. Ya nadie los
 * lee, pero siguen siendo credenciales vivas en una computadora pública. Se borran
 * al cargar; solo las claves de Cognito, nada más de localStorage.
 */
try {
  const viejas: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k?.startsWith("CognitoIdentityServiceProvider.")) viejas.push(k);
  }
  viejas.forEach((k) => localStorage.removeItem(k));
} catch {
  /* sin acceso a localStorage (modo privado estricto): no hay nada que limpiar */
}

export interface CognitoAuth {
  idToken: string;
  claims: Record<string, any>;
}

/**
 * Resultado de un intento de login. `newPasswordRequired` es el primer login de
 * las cuentas creadas por admin: nacen con contraseña temporal y Cognito obliga a
 * definir una nueva antes de emitir tokens. `complete` continúa ese reto.
 */
export type SignInResult =
  | ({ kind: "ok" } & CognitoAuth)
  | { kind: "newPasswordRequired"; complete: (newPassword: string) => Promise<SignInResult> }
  /** El personal restableció la contraseña: hace falta el código del correo. */
  | { kind: "resetRequired" };

/** Los claims custom:* (role, org_id, site_id, db_user_id) viajan en el id token. */
async function fromSession(): Promise<{ kind: "ok" } & CognitoAuth> {
  const idToken = (await fetchAuthSession()).tokens?.idToken;
  if (!idToken) {
    // No debería ocurrir: solo se llama tras un isSignedIn true.
    throw new Error("Cognito no devolvió un id token tras iniciar sesión.");
  }
  return { kind: "ok", idToken: idToken.toString(), claims: idToken.payload as Record<string, any> };
}

async function interpretar(salida: SignInOutput): Promise<SignInResult> {
  if (salida.isSignedIn) return fromSession();

  if (salida.nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
    return {
      kind: "newPasswordRequired",
      complete: async (newPassword: string) =>
        /*
         * Sin `userAttributes`. Cognito devuelve entre los atributos requeridos
         * algunos INMUTABLES (email_verified, etc.) que no se pueden reenviar;
         * omitirlos evita el error "Cannot modify an already provided email".
         */
        interpretar(await confirmSignIn({ challengeResponse: newPassword })),
    };
  }

  /*
   * La cuenta quedó en RESET_REQUIRED porque el personal pulsó «Restablecer
   * contraseña». No hay sesión que abrir: la persona tiene que usar el código que
   * le llegó por correo. Antes esto caía abajo y se le enseñaba «paso no
   * soportado: RESET_PASSWORD», que no le decía qué hacer.
   */
  if (salida.nextStep.signInStep === "RESET_PASSWORD") return { kind: "resetRequired" };

  /*
   * Cualquier otro reto (MFA, TOTP...) deja al usuario SIN sesión. Fallar aquí es
   * obligatorio: `wrap()` en lib/auth.ts instala la sesión con lo que le devuelvan,
   * así que devolver algo distinto de "ok" sin serlo abriría la plataforma sin token.
   */
  throw new Error(`Este inicio de sesión requiere un paso no soportado: ${salida.nextStep.signInStep}`);
}

export async function signIn(username: string, password: string): Promise<SignInResult> {
  const intento = () => amplifySignIn({ username, password });

  let salida: SignInOutput;
  try {
    salida = await intento();
  } catch (error) {
    /*
     * v6 se niega a iniciar sesión si queda una sesión vieja cacheada; v5 la
     * reemplazaba callada. Pasa de verdad aquí: `volverAlLogin()` recarga la página
     * justo después de `logout()`, así que el cierre de sesión de Amplify puede no
     * haber terminado. Se limpia y se reintenta una vez.
     */
    if ((error as { name?: string })?.name !== "UserAlreadyAuthenticatedException") {
      throw error;
    }
    await amplifySignOut();
    salida = await intento();
  }

  return interpretar(salida);
}

/** Envía el código de recuperación al correo verificado de la cuenta. Cognito no
 * revela si una cuenta existe cuando preventUserExistenceErrors está activo. */
export async function requestPasswordReset(username: string): Promise<void> {
  await resetPassword({ username });
}

/** Confirma el código recibido y fija la nueva contraseña directamente en Cognito. */
export async function confirmPasswordReset(
  username: string,
  code: string,
  newPassword: string
): Promise<void> {
  await confirmResetPassword({ username, confirmationCode: code, newPassword });
}

/**
 * Devuelve un id token FRESCO, renovándolo si hiciera falta.
 *
 * Los id tokens de Cognito duran 60 minutos; el refresh token, 30 días.
 * `fetchAuthSession()` usa el segundo para renovar el primero de forma transparente,
 * y eso es lo que evita que a una bibliotecaria se le caiga la sesión a mitad de un
 * registro cada hora.
 *
 * `null` significa que ya no se puede renovar (refresh token vencido o sesión
 * cerrada en otro sitio): ahí sí hay que volver a entrar.
 *
 * `forzar` pide a Cognito un token nuevo aunque el cacheado parezca vigente. Es lo
 * que toca tras un 401: si el servidor rechazó el token, devolver el MISMO token
 * cacheado solo repite el 401.
 */
export async function refreshIdToken(forzar = false): Promise<string | null> {
  try {
    return (await fetchAuthSession({ forceRefresh: forzar })).tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

/**
 * Cierra la sesión que Amplify cachea en el sessionStorage de esta pestaña.
 *
 * Es asíncrono (en v6 lo es) y quien llama —`logout()` en lib/auth.ts— es síncrono
 * y puede recargar la página acto seguido. Por eso no se espera: lo que de verdad
 * cierra la sesión de la aplicación es el borrado de sessionStorage, que sí es
 * inmediato. Si el borrado de Amplify no llega a completarse, el `signIn` siguiente
 * lo resuelve con el reintento de arriba.
 */
export function signOut(): void {
  void amplifySignOut().catch(() => {
    /* puede no haber sesión cacheada; no es un error */
  });
}
